import { CanvasRenderer, rgbCss } from '@/renderer/CanvasRenderer';
import type { BlendMode } from '@/state/appState';
import type { Project } from '@/types/engine';

export interface ThumbnailLook {
  blendMode?: BlendMode;
  roughness?: number;
  brightness?: number;
}

/** Renders a project's strings small, for design cards. Returns null where a 2D canvas is unavailable. */
export function renderThumbnail(project: Project, size = 320, look: ThumbnailLook = {}): string | null {
  const tw = project.target.width;
  const th = project.target.height;
  if (!(tw > 0 && th > 0) || typeof document === 'undefined') return null;
  const scale = size / Math.max(tw, th);
  const width = Math.max(1, Math.round(tw * scale));
  const height = Math.max(1, Math.round(th * scale));
  const canvas = document.createElement('canvas');
  let renderer: CanvasRenderer;
  try {
    renderer = new CanvasRenderer(canvas);
  } catch {
    return null;
  }
  renderer.setViewport(width, height, 1);
  renderer.setTargetSize(tw, th);
  renderer.setTransform({ scale, offsetX: 0, offsetY: 0 });
  const pins = new Float32Array(project.pinCoordinates.length * 2);
  project.pinCoordinates.forEach(([x, y], i) => {
    pins[i * 2] = x;
    pins[i * 2 + 1] = y;
  });
  const sequence = new Uint16Array(project.sequence.length * 2);
  project.sequence.forEach(([a, b], i) => {
    sequence[i * 2] = a;
    sequence[i * 2 + 1] = b;
  });
  renderer.setPins(pins);
  renderer.setSequence(sequence);
  const r = project.render;
  renderer.setStyle({
    color: r.stringColor,
    opacity: r.stringOpacity,
    // Keep a string at least three quarters of a pixel wide, or small cards go blank.
    thickness: Math.max(0.75, r.stringThickness * scale),
    background: r.backgroundColor,
    blendMode: look.blendMode ?? 'source-over',
    roughness: look.roughness ?? 0,
    brightness: look.brightness ?? 0,
  });
  renderer.draw(project.sequence.length);
  if (project.sequence.length === 0 && pins.length > 0) {
    // An empty frame still shows its pins, so the card reads as a design and not a blank.
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = rgbCss(r.pinColor);
      const radius = Math.max(0.8, r.pinRadius * scale);
      for (let i = 0; i < pins.length; i += 2) {
        ctx.beginPath();
        ctx.arc(pins[i] * scale, pins[i + 1] * scale, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
